import React from 'react';
import { t } from '@lingui/macro';
import { z } from 'zod';

import { Box } from '@rover/kibble/core';
import { useKibbleForm } from '@rover/kibble/forms';
import SwitchField from '@rover/kibble/forms/fields/SwitchField';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { getCurrencyAndLocaleConfig } from '@rover/utilities/currency';

import { PriceToggleClick } from './analyticsEvents';

type Props = {
  currencyCode: string;
  unit: string;
  onChange: (arg0: boolean) => void;
  value: boolean;
  disabled?: boolean;
  source: string;
};

const PriceToggle = ({
  value,
  unit,
  currencyCode,
  onChange,
  source,
  disabled,
}: Props): JSX.Element => {
  const { user } = useCurrentUser();

  const personOpk = user?.opk as string;

  const handleChange = (newValue: boolean): void => {
    emitAnalyticsEvent(
      new PriceToggleClick({
        partner: 'brighthorizons',
        source,
        personId: personOpk,
        newState: newValue ? 'currency' : 'credits',
      })
    );

    onChange(newValue);
  };

  const currencySymbol = getCurrencyAndLocaleConfig(currencyCode).currencyConfig.symbol;

  const { configureField } = useKibbleForm({
    values: {
      priceInCurrency: value,
    },
    schema: z.object({
      priceInCurrency: z.boolean(),
    }),
  });

  return (
    <Box data-testid="price-toggle">
      <SwitchField
        primaryLabel={t`View price per ${unit} (${currencySymbol})`}
        useBoldPrimaryLabel
        primaryLabelSize="100"
        disabled={disabled}
        onChange={handleChange}
        {...configureField('priceInCurrency')}
      />
    </Box>
  );
};

export default PriceToggle;
